import { Badge } from 'react-bootstrap';
import './alias.css';

/**
 * Alias — the one way to render a BlobHub handle (session-object alias, workflow-definition alias,
 * credential alias, mission alias, …).
 *
 * The treatment is the platform's signature outlined pill (.title-tag-badge), the way the playground's
 * session-object list has always shown them. Before this component, the same kind of value appeared
 * four different ways depending on where you met it — that pill in the objects list, a muted gray chip
 * in widget headers, a bare monospace <code> in the scheduler, and plain body text in the debugger.
 *
 * A note for anyone reconciling this against the design system: the guide's identifier convention
 * ("wrap every machine value in <code> → monospace pink") lists alias among its examples, while its
 * badges section lists alias among what the outlined pill carries. The pill wins. <code> stays for
 * UUIDs, types, formats, and paths — values you read or copy — whereas an alias is a NAME the user
 * chose for an object, and it reads as a label, not as a literal.
 *
 * There is ONE exception, and it is deliberate: an alias that stands in for a missing display name
 * (an org, blob, or user with no `name`, rendered as a title or a link) is NOT a value in that slot —
 * it is the object's name. Those sites keep the surrounding typography and must not use this
 * component.
 *
 * Variants:
 *   default — the pill at its natural width, for property values, list rows, and running sentences.
 *   chip    — the same pill, allowed to ellipsize. For constrained surfaces (widget headers) where the
 *             alias shares a row with a title and must truncate rather than push the layout.
 */
export default function Alias({ value, variant = "inline", className = "", title }) {
  if ( null == value || "" === value ) {
    return null;
  }
  const classes = ["title-tag-badge", "bh-alias", "chip" === variant ? "bh-alias-chip" : null, className]
    .filter(Boolean)
    .join(" ");
  return (
    <Badge variant="secondary" className={classes} title={title || value}>{value}</Badge>
  );
}
